import { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import SolaLogo from '@/components/SolaLogo';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'sola-install-dismissed-at';
const DISMISS_DAYS = 7;
const HIDDEN_ROUTES = ['/admin', '/reset-password', '/verify-email'];

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (navigator as any).standalone === true);

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !(window as any).MSStream;
};

const isInIframe = () => {
  try { return window.self !== window.top; } catch { return true; }
};

const recentlyDismissed = () => {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  const ageMs = Date.now() - Number(ts);
  return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

const AppInstallBanner = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);

  useEffect(() => {
    if (isInIframe()) return; // never inside Lovable preview iframe
    if (isStandalone()) return;
    if (recentlyDismissed()) return;
    if (HIDDEN_ROUTES.some(r => location.pathname.startsWith(r))) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS doesn't fire beforeinstallprompt — show after a delay
    let iosTimer: number | undefined;
    if (isIos()) {
      iosTimer = window.setTimeout(() => setShow(true), 8000);
    }

    const installed = () => { setShow(false); setDeferredPrompt(null); };
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [location.pathname]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') setShow(false);
      else dismiss();
    } else if (isIos()) {
      setShowIosModal(true);
    }
  };

  if (!show && !showIosModal) return null;

  return (
    <>
      {show && (
        <div className="fixed bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-1.5rem)] max-w-md animate-fade-in">
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-card border border-border shadow-lg">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Download size={18} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Instale o Sola Notes</p>
              <p className="text-[11px] text-subtle truncate">Acesso rápido na sua tela inicial, sem abrir o navegador.</p>
            </div>
            <button
              onClick={install}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex-shrink-0"
            >
              Instalar
            </button>
            <button onClick={dismiss} className="p-1 text-subtle hover:text-foreground flex-shrink-0" aria-label="Fechar">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {showIosModal && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 animate-fade-in"
          onClick={() => setShowIosModal(false)}
        >
          <div
            className="w-full sm:max-w-sm bg-card rounded-t-3xl sm:rounded-2xl border border-border shadow-xl px-6 pt-6 max-h-[90dvh] sm:max-h-[85vh] overflow-y-auto overscroll-contain"
            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <SolaLogo size="sm" />
              <button onClick={() => setShowIosModal(false)} className="p-1.5 rounded-lg hover:bg-secondary text-subtle">
                <X size={18} />
              </button>
            </div>
            <h3 className="font-serif text-lg text-foreground mb-2">Instalar no iPhone</h3>
            <p className="text-sm text-subtle mb-5">
              O Safari não instala apps automaticamente. Em 2 toques você adiciona o Sola Notes na sua tela inicial:
            </p>
            <ol className="space-y-3 text-sm text-foreground">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <span className="flex-1">Toque em <Share size={14} className="inline mx-1 text-primary" /> <strong>Compartilhar</strong> na barra do Safari.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <span className="flex-1">Escolha <Plus size={14} className="inline mx-1 text-primary" /> <strong>Adicionar à Tela de Início</strong>.</span>
              </li>
            </ol>
            <button
              onClick={() => { setShowIosModal(false); dismiss(); }}
              className="mt-6 w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AppInstallBanner;
